import { formatDate, sortArray, getCleanedUrlParams } from '@utils';
import { ENTITIES_PERMISIONS } from 'components/common/PermissionsGate/constants';

import {
  FILTER_OPERATOR_ENUMS,
  FILTER_RULE_ENUMS,
  RESERVATIONS_TYPES,
} from '@constants';
import { RESERVATIONS_PAGE_SIZE } from './constants';

const allowedPermissions = [
  ENTITIES_PERMISIONS.ReadBookings,
  ENTITIES_PERMISIONS.ReadReservations,
];

export const getReservationPayload = (filters = {}) => {
  let payload = {};
  const dropdownReservationsFilterRules = [];

  dropdownReservationsFilterRules.push({
    field: 'isActive',
    operator: 0,
    type: 5,
    value: true,
  });

  if (filters?.isErfuddAlert) {
    dropdownReservationsFilterRules.push({
      field: 'isErfuddAlert',
      operator: 0,
      type: FILTER_RULE_ENUMS.DateBetween,
      value: '10',
    });

    const erfuddTypes = [
      RESERVATIONS_TYPES.onhold.value,
      RESERVATIONS_TYPES.reserved.value,
    ];
    dropdownReservationsFilterRules.push({
      type: FILTER_RULE_ENUMS.OrRuleType,
      value: erfuddTypes.map((typeValue) => ({
        field: 'type',
        operator: 0,
        type: 5,
        value: typeValue,
      })),
    });
  } else if (filters?.type) {
    // Convert comma-separated string to array if needed
    const typeValues = Array.isArray(filters.type)
      ? filters.type
      : filters.type.toString().split(',');

    if (typeValues.length > 1) {
      // Handle multiple types with OR condition
      dropdownReservationsFilterRules.push({
        type: FILTER_RULE_ENUMS.OrRuleType,
        value: typeValues.map((typeValue) => ({
          field: 'type',
          operator: 0,
          type: 5,
          value: Number(typeValue),
        })),
      });
    } else {
      dropdownReservationsFilterRules.push({
        field: 'type',
        operator: 0,
        type: 5,
        value: Number(typeValues[0]),
      });
    }
  } else {
    dropdownReservationsFilterRules.push({
      type: 5,
      field: 'type',
      operator: FILTER_OPERATOR_ENUMS['!='],
      value: 5, // != Booked
    });
  }
  if (filters?.agent) {
    dropdownReservationsFilterRules.push({
      field: 'agent',
      operator: 0,
      type: filters.agent === 'none' ? FILTER_RULE_ENUMS.isEmpty : 5,
      value: filters.agent,
    });
  }
  if (filters?.subscriptionType) {
    dropdownReservationsFilterRules.push({
      field: 'subscriptionType',
      operator: 0,
      type: 5,
      value: Number(filters.subscriptionType),
    });
  }

  payload = {
    type: 0,
    field: null,
    operator: null,
    value: dropdownReservationsFilterRules,
  };
  return payload;
};

const handleComments = (comments = [], agents = []) =>
  sortArray(comments, 'createdAt', 'desc')?.map((entry) => {
    const date = entry.editedAt || entry.createdAt;

    return {
      id: entry?.id,
      date: date ? formatDate(date, 'dd MMMM yyyy') : '-',
      time: date ? formatDate(date, 'HH:mm') : '-',
      name:
        agents?.find((agent) => agent.email === entry?.author)?.email ||
        entry?.author,
      comment: entry?.comment,
      edit: entry?.editedAt
        ? formatDate(entry?.editedAt, 'dd MMMM yyyy HH:mm')
        : '',
    };
  });

export const normalizeReservations = (data = [], reservationsEnums = []) => {
  if (!data.length > 0) {
    return [];
  }

  const reservations = data.map((item) => {
    return {
      ...item,
      agent: item?.agent || '-',
      note: item?.note || '',
      createdAt: item?.createdAt
        ? formatDate(item?.createdAt, 'dd/MM/yyyy')
        : '-',
      createdBy: item?.createdBy || '-',
      subscriptionId: item?.subscriptionId || '-',
      subscriptionType: item?.subscriptionType || null,
      updatedAt: item?.updatedAt
        ? formatDate(item?.updatedAt, 'dd/MM/yyyy HH:mm')
        : '-',
      updatedBy: item?.updatedBy || '-',
      vehicleInternalId: item?.vehicleInternalId || '',
      vehicleName: item?.vehicleName || '',
      vehiclePlate: item?.vehiclePlate || '',
      pipedriveId: item?.pipedriveId || '',
      type:
        reservationsEnums?.types?.find(
          (resEnum) => resEnum.value === item?.type
        )?.text || item?.type,
      isErfuddAlert: item?.isErfuddAlert || false,
      erfudd: item?.erfudd ? formatDate(item?.erfudd, 'dd/MM/yyyy') : '-',
    };
  });

  return reservations;
};

export const normalizeReservationsEnums = (data = {}) => {
  return {
    ...data,
    types: data?.types?.map((item) => {
      return {
        text: RESERVATIONS_TYPES?.[item.name]?.text || item.name,
        value: item.id,
        name: item.name,
      };
    }),
  };
};

export const normalizeReservationsAgents = (data) => {
  return data?.filter((agent) =>
    allowedPermissions.every((permission) =>
      agent.permissions.includes(permission)
    )
  );
};

export const normalizeReservationData = (data, comments, agents) => {
  if (!data) {
    return {};
  }

  return {
    ...data,
    agent: data?.agent || '',
    type: data.type,
    subscriptionType: data?.subscriptionType || '',
    createdAt: data.createdAt ? formatDate(data.createdAt, 'dd/MM/yyyy') : '-',
    updatedAt: data.updatedAt ? formatDate(data.updatedAt, 'dd/MM/yyyy') : '-',
    promisedDeliveryDate: [
      data?.promisedDeliveryDate || null,
      data?.promisedDeliveryDateUntil || null,
    ],
    comments: handleComments(comments, agents),
  };
};

export const normalizeReservationHistory = (data) => {
  if (!data) {
    return [];
  }

  const history = data
    ?.map((item) => {
      const isType = item?.field?.[0] === 'Type';

      if (item?.field?.[0] === 'Comments') return null;

      if (isType) {
        const oldValue = Object.values(RESERVATIONS_TYPES).find(
          (type) => type.value === item?.from
        )?.text;
        const newValue = Object.values(RESERVATIONS_TYPES).find(
          (type) => type.value === item?.to
        )?.text;

        return {
          ...item,
          createdBy: item?.actor,
          fieldName: item?.field?.[0] || '',
          newValue,
          oldValue,
        };
      }

      return {
        ...item,
        createdBy: item?.actor,
        fieldName: item?.field?.[0] || '',
        newValue: item?.to || '',
        oldValue: item?.from || '-',
      };
    })
    ?.filter((item) => item);
  return history;
};

export const getParamsAndPayloadReservations = ({
  allFilters,
  pageSize,
  page,
  orderBy,
}) => {
  const payload = getReservationPayload({
    ...allFilters,
  });
  const params = getCleanedUrlParams({
    ...allFilters,
    pageSize: parseInt(pageSize, 10) || RESERVATIONS_PAGE_SIZE,
    page,
    orderBy,
  });

  const urlParams = getCleanedUrlParams({
    pageSize: parseInt(pageSize, 10) || RESERVATIONS_PAGE_SIZE,
    page,
    orderBy,
    order: allFilters.order,
    query: allFilters.query,
  });

  return { params, payload, urlParams };
};
